import { GridCustomCellProps, GridItemChangeEvent } from '@progress/kendo-react-grid';
import React from 'react';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { CpsGridCell } from './CpsGridCell';
import { EMPTY_VALUE } from '@cs-cms/types';

type DropdownSizeUnionType = 'small' | 'medium' | 'large';

interface EditableDropdownCellProps extends GridCustomCellProps {
  onItemChange: (event: GridItemChangeEvent) => void;
  options: any[];
  dataItemKey?: string;
  textField?: string;
  size?: DropdownSizeUnionType;
  style?: React.CSSProperties;
}

export const EditableDropdownCell: React.FC<EditableDropdownCellProps> = props => {
  const field = props.field || EMPTY_VALUE;
  const fieldValue = props.dataItem[field];
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const getCurrentValue = () => {
    if (!fieldValue || !props.options || props.options.length === 0) {
      return null;
    }

    if (props.dataItemKey) {
      const dataItemId = props.dataItem.id;
      const dataItemKey = props.dataItemKey;
      const matchById = props.options.find(option => option[dataItemKey] === dataItemId);
      if (matchById) {
        return matchById;
      }
    }

    if (props.textField) {
      const textField = props.textField;
      const matchByText = props.options.find(option => option[textField] === fieldValue);
      if (matchByText) {
        return matchByText;
      }
    }

    return fieldValue;
  };

  const value = getCurrentValue();

  const getCellTitle = (): string => {
    if (value == null) {
      return '';
    }

    if (typeof value === 'object' && props.textField) {
      const textValue = value[props.textField];
      if (textValue != null) {
        return String(textValue);
      }
    }

    return String(value);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (!isOpen) {
        return;
      }

      const target = event.target as Node;

      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      const kendoPopups = Array.from(
        document.querySelectorAll('.k-animation-container-shown, .k-animation-container, .k-popup')
      );
      for (const popup of kendoPopups) {
        if (popup.contains(target)) {
          return;
        }
      }

      setIsOpen(false);
    };

    const handleScrollOutside = (event: Event) => {
      if (!isOpen) {
        return;
      }

      const target = event.target as Node;

      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      const kendoPopups = Array.from(
        document.querySelectorAll('.k-animation-container-shown, .k-animation-container, .k-popup, .k-list-scroller')
      );
      for (const popup of kendoPopups) {
        if (popup.contains(target)) {
          return;
        }
      }

      const scrollableParent = (target as Element).closest?.('.k-list-scroller, .k-animation-container, .k-popup');
      if (scrollableParent) {
        return;
      }

      setIsOpen(false);
    };

    const controller = new AbortController();

    document.addEventListener('mousedown', handleClickOutside, { signal: controller.signal });

    document.addEventListener('wheel', handleScrollOutside, { signal: controller.signal });
    document.addEventListener('scroll', handleScrollOutside, { signal: controller.signal, capture: true });

    return () => {
      controller.abort();
    };
  }, [isOpen]);

  const handleChange = (event: DropDownListChangeEvent): void => {
    const newEvent: any = {
      ...event,
      dataItem: props.dataItem,
      field
    };
    props.onItemChange(newEvent);
  };

  const { style } = props;

  return (
    <CpsGridCell
      dataItem={props.dataItem}
      field={props.field}
      dataIndex={props.dataIndex}
      id={props.id}
      tdProps={props.tdProps}
      columnPosition={props.columnPosition}
      onChange={props.onChange}
      className={props.className}
      colSpan={props.colSpan}
      ariaColumnIndex={props.ariaColumnIndex}
      isSelected={props.isSelected}
      rowType={props.rowType}
      level={props.level}
      expanded={props.expanded}
      title={getCellTitle()}
    >
      <div ref={dropdownRef}>
        <DropDownList
          disabled={false}
          size={props.size}
          onChange={handleChange}
          dataItemKey={props.dataItemKey}
          textField={props.textField}
          data={props.options}
          value={value}
          opened={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          style={style}
        />
      </div>
    </CpsGridCell>
  );
};
